import React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { SurveyQuestionType } from 'shared/types';
import { StyledSlider } from './style';

type SliderQuestionProps = {
  question: SurveyQuestionType;
  answer: number;
  brandColor: string;
  textColor: string;
};

type SliderMarkerProps = {
  sliderValue: number;
  grid: number;
  position: number;
  allowDecimals: boolean;
  decimalPoints: '1' | '2';
};

const SliderQuestion = ({
  question,
  answer,
  brandColor,
  textColor,
}: SliderQuestionProps) => {
  const [left, setLeft] = React.useState(8);
  const thumbRef = React.useRef<HTMLDivElement>(null);
  const hoverRef = React.useRef<HTMLDivElement>(null);
  const sliderData = question.type_info?.meta?.slider;
  const min = sliderData?.min || 0;
  const max = sliderData?.max || 100;
  const step = sliderData?.grid_lines || 5;
  const value = [answer || 0];
  const isInput = sliderData?.numerical_input_box || false;

  React.useLayoutEffect(() => {
    if (hoverRef.current && thumbRef.current) {
      const hoverClient = hoverRef.current?.getBoundingClientRect();
      const thumbClient = thumbRef.current?.getBoundingClientRect();
      const leftPos = (hoverClient?.width - thumbClient?.width) / 2 || 8;
      setLeft(leftPos);
    }
  }, [value]);

  const getPercents = (val: number) => {
    if (val === min) return 100;
    if (val >= max) return 0;
    const maxSteps = max - min;
    const percentPerStep = 100 / maxSteps;
    return 100 - percentPerStep * (val - min);
  };

  const { markers, markerPos, steps } = React.useMemo(() => {
    const steps = (max - min) / step;
    const marker: number[] = [];
    const markerPos: number[] = [];
    new Array(step + 1).fill(0).forEach((_, i) => {
      const newMark = steps * i + min;
      marker.push(newMark <= max ? newMark : max);
      markerPos.push(getPercents(newMark));
    });
    return { markers: marker, markerPos: markerPos, steps };
  }, [step, min, max]);

  return (
    <StyledSlider brandColor={brandColor} textColor={textColor}>
      <SliderPrimitive.Root
        className="slider-root"
        value={value}
        min={min}
        max={max}
        step={steps}
        disabled
      >
        <SliderPrimitive.Track className="slider-track">
          <SliderPrimitive.Range className="slider-range" />
          <div className="marker-wrap">
            {markers.map((grid, i) => (
              <SliderMarker
                sliderValue={value[0]}
                grid={grid}
                position={markerPos[i]}
                allowDecimals={!!sliderData?.allow_decimal}
                decimalPoints={sliderData?.decimal_points || '2'}
                key={i}
              />
            ))}
          </div>
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className="slider-thumb"
          aria-label="Volume"
          ref={thumbRef}
        >
          <span
            className="thumb-hover"
            ref={hoverRef}
            style={{ left: `-${left}px` }}
          >
            {value}
          </span>
        </SliderPrimitive.Thumb>
      </SliderPrimitive.Root>
      {isInput && <div className="input-basic slider-input">{value[0]}</div>}
    </StyledSlider>
  );
};

const SliderMarker = ({
  sliderValue,
  grid,
  position,
  allowDecimals,
  decimalPoints,
}: SliderMarkerProps) => {
  const gridValue =
    grid % 1 !== 0
      ? Number(grid.toFixed(allowDecimals ? Number(decimalPoints) : 0))
      : grid;

  return (
    <span
      className={`marker ${sliderValue === grid ? 'active' : ''}`}
      style={{
        right: `${position - 1}%`,
      }}
    >
      {gridValue}
    </span>
  );
};

export default SliderQuestion;
